import CopyBlock from '../components/CopyBlock';

export default function BuilderDocs({ navigate }) {
  const API = 'https://invokeapi-production.up.railway.app';

  const s = {
    container: { maxWidth: 800, margin: '0 auto' },
    h1: { fontSize: 28, fontWeight: 700, marginBottom: 8 },
    sub: { fontSize: 15, lineHeight: 1.7, color: 'var(--muted)', marginBottom: 40 },
    h2: { fontSize: 20, fontWeight: 600, marginTop: 40, marginBottom: 16, color: 'var(--text)' },
    h3: { fontSize: 16, fontWeight: 600, marginTop: 24, marginBottom: 8, color: 'var(--text)' },
    p: { fontSize: 14, lineHeight: 1.7, color: 'var(--muted)', marginBottom: 12 },
    ul: { paddingLeft: 20, marginBottom: 16 },
    li: { fontSize: 14, lineHeight: 1.8, color: 'var(--muted)' },
    stepRow: {
      display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 20,
    },
    stepNum: {
      flexShrink: 0, width: 32, height: 32, borderRadius: '50%',
      background: 'var(--accent)', color: '#fff', display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700,
    },
    stepContent: { flex: 1, paddingTop: 4 },
    stepTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4 },
    stepDesc: { fontSize: 14, color: 'var(--muted)', lineHeight: 1.6 },
    codeBlock: {
      background: 'var(--bg)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)', padding: 16, marginBottom: 24,
      overflowX: 'auto', fontFamily: 'monospace', fontSize: 13,
      lineHeight: 1.6, color: 'var(--accent2)', whiteSpace: 'pre',
    },
    codeLabel: {
      fontSize: 12, fontWeight: 600, color: 'var(--muted)',
      textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
    },
    table: {
      width: '100%', borderCollapse: 'collapse', marginBottom: 24,
    },
    th: {
      textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--muted)',
      textTransform: 'uppercase', letterSpacing: '0.08em',
      padding: '10px 12px', borderBottom: '1px solid var(--border)',
    },
    td: {
      fontSize: 13, padding: '10px 12px', color: 'var(--text)',
      borderBottom: '1px solid var(--border)',
    },
    tdMono: {
      fontSize: 13, padding: '10px 12px', color: 'var(--accent2)',
      borderBottom: '1px solid var(--border)', fontFamily: 'monospace',
    },
    tdMuted: {
      fontSize: 13, padding: '10px 12px', color: 'var(--muted)',
      borderBottom: '1px solid var(--border)',
    },
    tipCard: {
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: 20, marginBottom: 12,
      display: 'flex', gap: 12, alignItems: 'flex-start',
    },
    tipIcon: { flexShrink: 0, fontSize: 14, color: 'var(--accent)', fontWeight: 700 },
    tipText: { fontSize: 14, color: 'var(--muted)', lineHeight: 1.6 },
    heroBtn: {
      display: 'inline-block', padding: '10px 22px', borderRadius: 'var(--radius-sm)',
      border: 'none', background: 'var(--accent)', color: '#fff',
      fontWeight: 600, fontSize: 14, cursor: 'pointer', marginTop: 8,
    },
  };

  const endpoints = [
    { method: 'POST', path: '/v1/builder/builds', desc: 'Create a new build' },
    { method: 'GET', path: '/v1/builder/builds', desc: 'List all your builds' },
    { method: 'GET', path: '/v1/builder/builds/:slug', desc: 'Get a single build' },
    { method: 'PATCH', path: '/v1/builder/builds/:slug', desc: 'Update a build (description, tags, pricing, endpoint)' },
    { method: 'DELETE', path: '/v1/builder/builds/:slug', desc: 'Delete a build' },
    { method: 'POST', path: '/v1/builder/builds/:slug/toggle', desc: 'Toggle build live/offline' },
    { method: 'POST', path: '/v1/builder/builds/:slug/scan', desc: 'Run security scan + validate all tools' },
    { method: 'POST', path: '/v1/builder/builds/:slug/tools', desc: 'Add a tool to a build' },
    { method: 'GET', path: '/v1/builder/builds/:slug/tools', desc: 'List tools for a build' },
    { method: 'PATCH', path: '/v1/builder/builds/:slug/tools/:toolSlug', desc: 'Update a tool' },
    { method: 'DELETE', path: '/v1/builder/builds/:slug/tools/:toolSlug', desc: 'Delete a tool' },
    { method: 'POST', path: '/v1/builder/builds/:slug/job-types', desc: 'Create a job type (async task)' },
    { method: 'GET', path: '/v1/builder/builds/:slug/job-types', desc: 'List job types' },
    { method: 'PATCH', path: '/v1/builder/builds/:slug/job-types/:typeSlug', desc: 'Update a job type' },
    { method: 'DELETE', path: '/v1/builder/builds/:slug/job-types/:typeSlug', desc: 'Delete a job type' },
  ];

  const tips = [
    'Your endpoint must expose GET /health returning HTTP 200 to appear as live.',
    'Platform takes 30%, you keep 70% of every call.',
    'Use the same API key you use for consuming builds.',
    'Tags help users discover your build \u2014 use relevant keywords.',
    'Run a security scan before going live \u2014 it checks HTTPS, health endpoint, error handling, data leaks, and validates every tool.',
    'Your build can be anything that responds to HTTP: a local AI model (Ollama, vLLM), an MCP server, a database API, a GPU pipeline, or a simple Python script.',
    'Enable file uploads to let consumers send images, PDFs, and other files. Set fileInstructions to guide them on what to send.',
    'Use jobs for heavy compute (backtests, simulations, AI generation). Consumers pay upfront, you get paid when the job completes.',
    'Always send progress updates from long jobs \u2014 consumers see a live progress bar while waiting.',
  ];

  const methodColor = (m) => {
    if (m === 'GET') return '#22c55e';
    if (m === 'POST') return 'var(--accent)';
    if (m === 'PATCH') return '#f59e0b';
    if (m === 'DELETE') return '#ef4444';
    return 'var(--text)';
  };

  return (
    <div style={s.container}>
      {/* Hero */}
      <h1 style={s.h1}>Builder API</h1>
      <p style={s.sub}>
        Manage your builds programmatically. Use your API key to create, update, and manage builds from any agent or script.
      </p>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 32 }}>
        Prefer guided setup? <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('builder-farm')}>Get an AI-generated prompt</span> that walks you through building, testing, and publishing step by step.
      </p>

      {/* What is a Build */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24, marginBottom: 32 }}>
        <h2 style={{ ...s.h2, marginTop: 0 }}>What is a build?</h2>
        <p style={s.p}>
          A <strong style={{ color: 'var(--text)' }}>build</strong> is any service you host that accepts API calls through our proxy.
          Think of it as a monetized endpoint — you run the server, we handle billing, authentication, and marketplace discovery.
        </p>
        <p style={{ ...s.p, marginBottom: 0 }}>
          Builds can be anything: an <strong style={{ color: 'var(--text)' }}>AI model</strong> trained on specialized data,
          an <strong style={{ color: 'var(--text)' }}>MCP tool server</strong> that agents can discover and use,
          a <strong style={{ color: 'var(--text)' }}>curated database</strong> with a query API,
          a <strong style={{ color: 'var(--text)' }}>GPU computation service</strong>,
          or any HTTP endpoint that does something useful. Your data stays on your machine — consumers pay per call, you get 70%.
        </p>
      </div>

      {/* MCP Setup — unified builder+consumer */}
      <div style={{ background: 'var(--bg2)', border: '2px solid var(--accent)', borderRadius: 'var(--radius)', padding: 24, marginBottom: 32 }}>
        <h2 style={{ ...s.h2, marginTop: 0, color: 'var(--accent)' }}>MCP Setup (recommended)</h2>
        <p style={s.p}>
          Connect invokeAPI to <strong style={{ color: 'var(--text)' }}>Claude Desktop</strong>, <strong style={{ color: 'var(--text)' }}>Claude Code</strong>, or any MCP client.
          One connection lets you create builds, manage tools, go live, <em>and</em> call other builds — all from chat.
        </p>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Claude Desktop — add to claude_desktop_config.json:</div>
        <CopyBlock label="Copy" text={`{
  "mcpServers": {
    "invokeAPI": {
      "command": "npx",
      "args": [
        "mcp-remote",
        "https://invokeapi-production.up.railway.app/mcp?key=YOUR_API_KEY"
      ]
    }
  }
}`} />
        <p style={{ ...s.p, marginTop: 8, marginBottom: 12, fontSize: 12, color: 'var(--muted)' }}>
          Requires Node.js installed. First run may take a moment while npx downloads mcp-remote.
        </p>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 16, marginBottom: 8 }}>Claude Code — run in terminal:</div>
        <CopyBlock label="Copy" text={`claude mcp add invokeapi \\
  --transport http \\
  "https://invokeapi-production.up.railway.app/mcp" \\
  --header "Authorization: Bearer YOUR_API_KEY"`} />
        <p style={{ ...s.p, marginTop: 12, marginBottom: 0, fontSize: 13 }}>
          After setup, just tell Claude: <em>"Create a build called My API with endpoint https://my-server.com"</em> — it handles the rest.
        </p>
      </div>

      {/* Quick Start */}
      <h2 style={s.h2}>Quick Start (REST API)</h2>

      <div style={s.stepRow}>
        <div style={s.stepNum}>1</div>
        <div style={s.stepContent}>
          <div style={s.stepTitle}>Sign up and get your API key</div>
          <div style={s.stepDesc}>
            Create an account at{' '}
            <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('getkey')}>invoke-api.com</span>{' '}
            and copy your API key from the dashboard.
          </div>
        </div>
      </div>

      <div style={s.stepRow}>
        <div style={s.stepNum}>2</div>
        <div style={s.stepContent}>
          <div style={s.stepTitle}>Create a build</div>
          <div style={s.stepDesc}>POST your build details to the API. You'll get back a build ID and webhook secret.</div>
        </div>
      </div>

      <div style={s.stepRow}>
        <div style={s.stepNum}>3</div>
        <div style={s.stepContent}>
          <div style={s.stepTitle}>Add tools</div>
          <div style={s.stepDesc}>Define individual tools within your build, each with its own pricing and description.</div>
        </div>
      </div>

      <div style={s.stepRow}>
        <div style={s.stepNum}>4</div>
        <div style={s.stepContent}>
          <div style={s.stepTitle}>Go live</div>
          <div style={s.stepDesc}>Toggle your build online once your /health endpoint returns 200. Consumers can start calling immediately.</div>
        </div>
      </div>

      {/* Code Examples */}
      <h2 style={s.h2}>Code Examples</h2>

      <div style={s.codeLabel}>Create a build</div>
      <pre style={s.codeBlock}>{`curl -X POST ${API}/v1/builder/builds \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My Analytics API",
    "slug": "my-analytics",
    "description": "Real-time market analytics",
    "endpointUrl": "https://your-server.com",
    "pricePerCall": 0.10,
    "tags": "analytics,data",
    "exampleQueries": ["Get BTC price", "Run analysis"]
  }'`}</pre>

      <div style={s.codeLabel}>Add a tool</div>
      <pre style={s.codeBlock}>{`curl -X POST ${API}/v1/builder/builds/my-analytics/tools \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Get Price",
    "slug": "get-price",
    "description": "Get current price for a symbol",
    "command": "{\\"type\\": \\"price\\", \\"symbol\\": \\"BTCUSDT\\"}",
    "pricePerCall": 0.05,
    "category": "query"
  }'`}</pre>

      <div style={s.codeLabel}>Update your build</div>
      <pre style={s.codeBlock}>{`curl -X PATCH ${API}/v1/builder/builds/my-analytics \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"description": "Updated description", "tags": "new,tags"}'`}</pre>

      <div style={s.codeLabel}>Go live</div>
      <pre style={s.codeBlock}>{`curl -X POST ${API}/v1/builder/builds/my-analytics/toggle \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</pre>

      <div style={s.codeLabel}>List your builds</div>
      <pre style={s.codeBlock}>{`curl ${API}/v1/builder/builds \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</pre>

      {/* File Uploads */}
      <h2 style={s.h2}>File Upload Support</h2>
      <p style={s.p}>
        Builds can accept file uploads (images, PDFs, audio, video, etc.) from consumers.
        Files are sent as <code style={{ fontFamily: 'monospace', color: 'var(--accent2)', fontSize: 13 }}>multipart/form-data</code> and
        forwarded to your endpoint with zero parsing — you get the raw multipart request exactly as the consumer sent it.
      </p>

      <h3 style={s.h3}>Enable file uploads</h3>
      <p style={s.p}>
        Toggle "Enable Files" in your dashboard, or configure via API:
      </p>
      <pre style={s.codeBlock}>{`curl -X PATCH ${API}/v1/builder/builds/my-build \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "acceptsFiles": true,
    "acceptedFileTypes": "image/*,application/pdf,audio/*",
    "maxFileSize": 10485760,
    "fileInstructions": "Send an image for analysis. Max 10MB. Supported: JPEG, PNG, PDF."
  }'`}</pre>

      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Field</th>
              <th style={s.th}>Type</th>
              <th style={s.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={s.tdMono}>acceptsFiles</td><td style={s.td}>boolean</td><td style={s.tdMuted}>Enable/disable file uploads</td></tr>
            <tr><td style={s.tdMono}>acceptedFileTypes</td><td style={s.td}>string</td><td style={s.tdMuted}>Comma-separated MIME patterns (e.g. "image/*,application/pdf")</td></tr>
            <tr><td style={s.tdMono}>maxFileSize</td><td style={s.td}>number</td><td style={s.tdMuted}>Max file size in bytes (default 5MB, max 50MB)</td></tr>
            <tr><td style={s.tdMono}>fileInstructions</td><td style={s.td}>string</td><td style={s.tdMuted}>Instructions shown to consumers about what files to send</td></tr>
          </tbody>
        </table>
      </div>

      <h3 style={s.h3}>How your endpoint receives files</h3>
      <p style={s.p}>
        When a consumer uploads files, your endpoint receives a standard <code style={{ fontFamily: 'monospace', color: 'var(--accent2)', fontSize: 13 }}>multipart/form-data</code> POST request:
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong style={{ color: 'var(--text)' }}>_params</strong> — JSON string with any additional parameters</li>
        <li style={s.li}><strong style={{ color: 'var(--text)' }}>file</strong> — One or more file parts (standard multipart file fields)</li>
      </ul>
      <div style={s.codeLabel}>Python (Flask) example</div>
      <pre style={s.codeBlock}>{`@app.route('/api', methods=['POST'])
def handle():
    params = json.loads(request.form.get('_params', '{}'))
    files = request.files.getlist('file')
    for f in files:
        data = f.read()
        # Process file: f.filename, f.content_type, data
    return jsonify({"processed": len(files)})`}</pre>

      <div style={s.codeLabel}>Node.js (Express + multer) example</div>
      <pre style={s.codeBlock}>{`const multer = require('multer');
const upload = multer({ limits: { fileSize: 10 * 1024 * 1024 } });

app.post('/api', upload.array('file'), (req, res) => {
  const params = JSON.parse(req.body._params || '{}');
  const files = req.files; // Array of { buffer, originalname, mimetype }
  res.json({ processed: files.length });
});`}</pre>

      <div style={s.tipCard}>
        <div style={s.tipIcon}>*</div>
        <div style={s.tipText}>
          <strong style={{ color: 'var(--text)' }}>MCP clients</strong> (Claude Desktop, Claude Code) send files as base64-encoded JSON.
          The proxy automatically converts them to multipart before forwarding to your endpoint — your code handles multipart the same way regardless of the source.
        </div>
      </div>

      {/* Async Jobs */}
      <h2 style={s.h2}>Async Jobs (Long-Running Tasks)</h2>
      <p style={s.p}>
        For tasks that take more than 30 seconds — backtests, simulations, report generation, AI pipelines —
        use <strong style={{ color: 'var(--text)' }}>Jobs</strong>. Consumers submit a job, poll for progress, and get results when done.
        Jobs can run up to 10 minutes and are priced in dollars instead of cents.
      </p>

      <h3 style={s.h3}>How jobs work</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        <div style={s.stepRow}>
          <div style={s.stepNum}>1</div>
          <div style={s.stepContent}>
            <div style={s.stepTitle}>Consumer submits a job</div>
            <div style={s.stepDesc}>POST to /v1/jobs/your-build with job type and parameters. Credits are reserved upfront.</div>
          </div>
        </div>
        <div style={s.stepRow}>
          <div style={s.stepNum}>2</div>
          <div style={s.stepContent}>
            <div style={s.stepTitle}>Proxy forwards to your /jobs endpoint</div>
            <div style={s.stepDesc}>Your server receives the job details via POST with callback URL in headers. Return 202 immediately.</div>
          </div>
        </div>
        <div style={s.stepRow}>
          <div style={s.stepNum}>3</div>
          <div style={s.stepContent}>
            <div style={s.stepTitle}>Your server does the work</div>
            <div style={s.stepDesc}>Run the task in a background thread. Send progress updates to the callback URL as you go.</div>
          </div>
        </div>
        <div style={s.stepRow}>
          <div style={s.stepNum}>4</div>
          <div style={s.stepContent}>
            <div style={s.stepTitle}>POST result to callback</div>
            <div style={s.stepDesc}>When done, POST the result JSON to the callback URL. Consumer gets charged, you get paid (70%).</div>
          </div>
        </div>
      </div>

      <h3 style={s.h3}>Create a job type</h3>
      <pre style={s.codeBlock}>{`curl -X POST ${API}/v1/builder/builds/my-build/job-types \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Strategy Backtest",
    "slug": "backtest",
    "description": "Run a trading strategy backtest across historical data",
    "price": 2.00,
    "timeout_ms": 300000,
    "category": "analysis",
    "example_params": {"strategy": "drc", "symbol": "BTCUSDT", "timeframe": "1d"}
  }'`}</pre>

      <h3 style={s.h3}>Your /jobs endpoint</h3>
      <p style={s.p}>
        When a consumer submits a job, the proxy POSTs to your endpoint at <code style={{ fontFamily: 'monospace', color: 'var(--accent2)', fontSize: 13 }}>/jobs</code>. Read these headers:
      </p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Header</th>
              <th style={s.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={s.tdMono}>x-invokeapi-job-id</td><td style={s.tdMuted}>Unique job ID (UUID)</td></tr>
            <tr><td style={s.tdMono}>x-invokeapi-job-type</td><td style={s.tdMuted}>Job type slug (e.g. "backtest")</td></tr>
            <tr><td style={s.tdMono}>x-invokeapi-callback</td><td style={s.tdMuted}>URL to POST progress/results back to</td></tr>
            <tr><td style={s.tdMono}>x-invokeapi-callback-secret</td><td style={s.tdMuted}>Secret to authenticate your callbacks</td></tr>
          </tbody>
        </table>
      </div>

      <div style={s.codeLabel}>Python example</div>
      <pre style={s.codeBlock}>{`import threading, requests

@app.route('/jobs', methods=['POST'])
def handle_job():
    job_id = request.headers.get('x-invokeapi-job-id')
    job_type = request.headers.get('x-invokeapi-job-type')
    callback = request.headers.get('x-invokeapi-callback')
    secret = request.headers.get('x-invokeapi-callback-secret')
    params = request.json or {}

    # Run in background thread
    threading.Thread(
        target=run_job,
        args=(job_id, job_type, callback, secret, params)
    ).start()

    return '', 202  # Return immediately

def run_job(job_id, job_type, callback, secret, params):
    headers = {'x-invokeapi-secret': secret, 'Content-Type': 'application/json'}

    # Send progress updates
    requests.post(callback, json={
        'status': 'running', 'progress': 50,
        'progress_msg': 'Processing data...'
    }, headers=headers)

    # Do the actual work...
    result = do_heavy_computation(params)

    # Send final result
    requests.post(callback, json={
        'status': 'completed',
        'result': result
    }, headers=headers)`}</pre>

      <div style={s.tipCard}>
        <div style={s.tipIcon}>*</div>
        <div style={s.tipText}>
          <strong style={{ color: 'var(--text)' }}>Billing:</strong> Credits are reserved when the job is submitted.
          On completion, the consumer is charged and you earn 70%. If the job fails or times out, credits are automatically refunded.
          You can override the cost in your callback (capped at 1.5x the listed price).
        </div>
      </div>

      {/* Full API Reference */}
      <h2 style={s.h2}>Full API Reference</h2>
      <p style={s.p}>All endpoints require the <code style={{ fontFamily: 'monospace', color: 'var(--accent2)', fontSize: 13 }}>Authorization: Bearer YOUR_API_KEY</code> header.</p>

      <div style={{ overflowX: 'auto', marginBottom: 32 }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Method</th>
              <th style={s.th}>Path</th>
              <th style={s.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((ep, i) => (
              <tr key={i}>
                <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: 600, color: methodColor(ep.method), fontSize: 12 }}>
                  {ep.method}
                </td>
                <td style={s.tdMono}>{ep.path}</td>
                <td style={s.tdMuted}>{ep.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tips */}
      <h2 style={s.h2}>Tips</h2>
      {tips.map((tip, i) => (
        <div key={i} style={s.tipCard}>
          <div style={s.tipIcon}>*</div>
          <div style={s.tipText}>{tip}</div>
        </div>
      ))}

      <div style={{ marginTop: 40, marginBottom: 20, textAlign: 'center' }}>
        <button style={s.heroBtn} onClick={() => navigate('register')}>
          Publish your first build
        </button>
      </div>
    </div>
  );
}
